import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { EditorState } from "@codemirror/state";
import { EditorView, keymap, lineNumbers } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";
import { useEffect, useRef } from "react";

import { wikilinkExtensions } from "./wikilinkPlugin";

interface Props {
  initialDoc: string;
  onSave: (doc: string) => void;
  onNavigateWikilink: (target: string) => void;
}

// Direct styles statt class-mapping — robuster: Lezer-Tags werden zuverlässig
// erkannt und bekommen sofort visuelle Properties. CSS-Class-Mapping war
// abhängig vom Theme-Selektor-Spezifität-Kram, und greift hier nicht.
const mdHighlight = HighlightStyle.define([
  { tag: t.heading1, fontSize: "1.7em", fontWeight: "700", color: "#f4f4f5" },
  { tag: t.heading2, fontSize: "1.4em", fontWeight: "700", color: "#f4f4f5" },
  { tag: t.heading3, fontSize: "1.2em", fontWeight: "600", color: "#e4e4e7" },
  { tag: t.heading4, fontSize: "1.1em", fontWeight: "600", color: "#d4d4d8" },
  { tag: [t.heading5, t.heading6], fontWeight: "600", color: "#d4d4d8" },
  { tag: t.strong, fontWeight: "700", color: "#f4f4f5" },
  { tag: t.emphasis, fontStyle: "italic", color: "#d4d4d8" },
  { tag: t.strikethrough, textDecoration: "line-through", color: "#71717a" },
  { tag: t.link, color: "#60a5fa", textDecoration: "underline" },
  { tag: t.url, color: "#60a5fa" },
  { tag: t.monospace, color: "#fcd34d", fontFamily: "'JetBrains Mono', monospace" },
  { tag: t.quote, color: "#a1a1aa", fontStyle: "italic" },
  { tag: t.list, color: "#34d399" },
  { tag: t.meta, color: "#a78bfa" },
  { tag: t.processingInstruction, color: "#71717a" },
  { tag: t.contentSeparator, color: "#52525b" },
]);

const theme = EditorView.theme(
  {
    "&": { height: "100%", fontSize: "14px" },
    ".cm-content": {
      padding: "12px 16px",
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      caretColor: "rgb(110 231 183)",
    },
    ".cm-gutters": {
      backgroundColor: "rgba(0,0,0,0)",
      borderRight: "1px solid rgb(39 39 42)",
      color: "rgb(82 82 91)",
    },
    ".cm-line": { lineHeight: "1.6" },
    ".cm-wikilink": { color: "rgb(110 231 183)", textDecoration: "underline", cursor: "pointer" },
    ".cm-activeLine": { backgroundColor: "rgba(255,255,255,0.02)" },
    ".cm-activeLineGutter": { backgroundColor: "rgba(255,255,255,0.02)" },
    ".cm-cursor": { borderLeftColor: "rgb(110 231 183)" },
    "&.cm-focused": { outline: "none" },
    "&.cm-focused .cm-selectionBackground, ::selection": { backgroundColor: "rgba(110,231,183,0.2)" },
  },
  { dark: true },
);

export function NoteEditor({ initialDoc, onSave, onNavigateWikilink }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onSaveRef = useRef(onSave);
  const onNavRef = useRef(onNavigateWikilink);
  onSaveRef.current = onSave;
  onNavRef.current = onNavigateWikilink;

  useEffect(() => {
    if (!hostRef.current) return;

    const saveKeymap = keymap.of([
      {
        key: "Mod-s",
        preventDefault: true,
        run: (v) => {
          onSaveRef.current(v.state.doc.toString());
          return true;
        },
      },
    ]);

    const state = EditorState.create({
      doc: initialDoc,
      extensions: [
        lineNumbers(),
        history(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        saveKeymap,
        markdown({ base: markdownLanguage, codeLanguages: [] }),
        syntaxHighlighting(mdHighlight),
        ...wikilinkExtensions((target) => onNavRef.current(target)),
        theme,
        EditorView.lineWrapping,
      ],
    });

    const view = new EditorView({ state, parent: hostRef.current });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // initialDoc absichtlich nur beim Mount lesen — Updates fließen über replaceDoc()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // initialDoc-Wechsel (Note umgeschaltet) → Editor-Inhalt ersetzen
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const cur = view.state.doc.toString();
    if (cur === initialDoc) return;
    view.dispatch({
      changes: { from: 0, to: cur.length, insert: initialDoc },
    });
  }, [initialDoc]);

  return <div ref={hostRef} className="h-full overflow-hidden" />;
}
